import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useAuth } from 'contexts/auth';
import { useProvideSearch } from 'contexts/ui/search-context';
import {
  AnalyticsAction,
  AnalyticsCategory,
  useAnalytics,
} from 'lib/analytics';
import store, { getState } from 'store';
import { useGetSuggestionsQuery } from 'store/api/suggest';
import { sortFuzzyCollection } from 'store/models/search-suggestions/searchSuggestions.utils';

import { useSearchControls } from '../use-search-controls';
import { useSearchParams } from '../use-search-params';

import type { SearchRivalType } from 'api/endpoints/search-rivals/search-rivals.types';
import type { Suggestion } from 'api/endpoints/suggestions';
import type { SearchCallbackType } from 'contexts/ui/search-context/searchContext.types';
import type { Dispatch } from 'store/store.types';

export type SearchCounts = {
  cards?: number;
  rivals?: number;
  supportedRivals?: number;
  all: number;
};

export type useSearchViewReturnType = {
  counts: SearchCounts;
  rivals: {
    results: SearchRivalType[];
  };
  supportedRivals: {
    isFetching: boolean;
    results: Suggestion[];
  };
};

const selectSearchState = store.select(
  ({ search: { searchId, count, searchTerms } }) => ({
    searchId,
    count,
    searchTerms,
  }),
);

const selectRivalResults = store.select(({ searchRivals: { all } }) => ({
  rivalResults: all,
}));

const selectSearchModelParams = store.select(
  ({
    search: {
      searchTerms,
      lastUpdated,
      selectedRivalIds: selectedBoards,
      selectedTagIds: selectedTags,
    },
  }) => ({
    searchTerms,
    lastUpdatedFilter: lastUpdated,
    selectedBoards,
    selectedTags,
  }),
);

const useSearchView: () => useSearchViewReturnType = () => {
  useTranslation(['Card', 'Search']);

  const {
    search: { getResults, reset },
    tags: { fetchAndPopulateInstanceTags },
  } = useDispatch<Dispatch>();

  const {
    terms: termsParam,
    lastUpdated: lastUpdatedQueryParam,
    tags: tagsQueryParam,
    boards: boardsQueryParam,
  } = useSearchParams();

  const {
    setTerms,
    addTagFilter,
    toggleTagFilter,
    setLastUpdatedFilter,
    clearBoardFilters,
    clearTagFilters,
  } = useSearchControls();

  const { isCompanyLandingPageEnabled } = useAuth();
  const { searchId, count, searchTerms } = useSelector(selectSearchState);
  const { currentData: suggestionsData, isFetching: isFetchingSuggestions } =
    useGetSuggestionsQuery(
      { query: searchTerms, mode: 'entities' },
      { skip: !isCompanyLandingPageEnabled },
    );

  const { rivalResults } = useSelector(selectRivalResults);

  const rivalDomainsSet = new Set(
    rivalResults
      .map((rival) => rival.supportedRival?.domain)
      .filter((domain) => Boolean(domain)),
  );

  const filteredSupportedRivalResults = isCompanyLandingPageEnabled
    ? suggestionsData?.suggestions.filter(
        (suggestion) => !rivalDomainsSet.has(suggestion.domain),
      ) || []
    : [];

  const supportedRivalResults = sortFuzzyCollection(
    filteredSupportedRivalResults,
    searchTerms,
    (item) => item.name,
  );

  const search = useCallback<SearchCallbackType>(
    ({ terms, tagId, tagOp, reset = false } = {}) => {
      if (reset) {
        setTerms('');
        setLastUpdatedFilter(null);
        clearBoardFilters();
        clearTagFilters();
      }
      if (terms !== undefined) {
        setTerms(terms);
      } else if (tagId) {
        if (tagOp === 'add') {
          addTagFilter(tagId);
        } else {
          toggleTagFilter(tagId);
        }
      } else {
        getResults();
      }
    },
    [
      addTagFilter,
      getResults,
      setTerms,
      toggleTagFilter,
      setLastUpdatedFilter,
      clearBoardFilters,
      clearTagFilters,
    ],
  );

  const provideSearch = useProvideSearch();
  useEffect(() => {
    provideSearch(search);
    return () => {
      provideSearch(null);
    };
  }, [provideSearch, search]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  useEffect(() => {
    fetchAndPopulateInstanceTags();
  }, [fetchAndPopulateInstanceTags]);

  const queryParams = useMemo(
    () => ({
      searchTerms: termsParam,
      lastUpdatedFilter: lastUpdatedQueryParam,
      selectedBoards: boardsQueryParam,
      selectedTags: tagsQueryParam,
    }),
    [boardsQueryParam, lastUpdatedQueryParam, tagsQueryParam, termsParam],
  );

  // Perform initial search and listen to changes to the URL to
  // perform searches that are not yet reflected in the model
  const [isInitialSearch, setInitialSearch] = useState(true);
  useEffect(() => {
    const isUrlChanged = () => {
      const modelParams = selectSearchModelParams(getState() as never);
      return !isEqual(queryParams, modelParams);
    };
    if (isInitialSearch || isUrlChanged()) {
      getResults(queryParams);
    }
    setInitialSearch(false);
  }, [getResults, isInitialSearch, queryParams]);

  const prevSearchId = useRef<number | null>(null);
  const { logEvent } = useAnalytics();
  useEffect(() => {
    if (searchId && searchId !== prevSearchId.current) {
      const label = searchTerms || '*';
      const value = count;
      logEvent({
        event: {
          category: AnalyticsCategory.search,
          action: count ? AnalyticsAction.search : AnalyticsAction.searchEmpty,
          label,
          value,
        },
      });
    }
    prevSearchId.current = searchId;
  }, [count, logEvent, searchId, searchTerms]);

  const counts: SearchCounts = {
    cards: count,
    rivals: rivalResults.length,
    supportedRivals: supportedRivalResults.length,
    all: count + rivalResults.length + supportedRivalResults.length,
  };

  return {
    counts,
    rivals: {
      results: rivalResults,
    },
    supportedRivals: {
      isFetching: isFetchingSuggestions,
      results: supportedRivalResults,
    },
  };
};

export default useSearchView;
